import React from "react";
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemText,
    Chip,
    Fade,
    Alert,
} from "@mui/material";
import {
    Gavel as GavelIcon,
    ContactMail as ContactIcon,
    Info as InfoIcon,
} from "@mui/icons-material";
import SEO from "../../components/SEO/SEO";
import { pageMeta } from "../../seo/pageMeta";

const TermsOfService = () => {
    const sections = [
        {
            title: "1. Agreement to Terms",
            content: `By downloading, accessing, or using the Quran Chat Buddy mobile application ("App"), 
      you agree to be bound by these Terms of Service ("Terms"). If you do not agree to 
      these Terms, please do not use our App.`,
        },
        {
            title: "2. Description of Service",
            content: `Quran Chat Buddy is an AI-powered Islamic companion app that provides:`,
            list: [
                "Quran reading and interpretation assistance",
                "Prayer time reminders and tracking",
                "Islamic educational content and daily insights",
                "Social sharing features for verses and Islamic content",
                "Habit tracking for Islamic practices",
            ],
        },
        {
            title: "3. User Responsibilities",
            content: "You agree to:",
            list: [
                "Use the App in accordance with Islamic principles and values",
                "Provide accurate information when required",
                "Respect the rights and dignity of other users",
                "Not use the App for any unlawful or inappropriate purpose",
                "Keep your account information secure and confidential",
            ],
        },
        {
            title: "4. Prohibited Uses",
            content: "You may not use the App to:",
            list: [
                "Share content that contradicts Islamic teachings",
                "Harass, abuse, or harm other users",
                "Upload or share inappropriate or offensive content",
                "Attempt to reverse engineer or compromise the App's security",
                "Use the App for commercial purposes without permission",
            ],
        },
        {
            title: "5. Intellectual Property Rights",
            content: `The App, including its original content, features, and functionality, is owned by 
      Quran Chat Buddy and is protected by international copyright, trademark, patent, 
      trade secret, and other intellectual property laws.`,
        },

        {
            title: "6. Subscription and Billing",
            content:
                "Quran Chat Buddy offers auto-renewable subscription plans. Subscriptions are managed through RevenueCat and processed by the platform where you downloaded the app:",
            list: [
                "iOS (Apple): Payment is charged to your Apple ID account at confirmation of purchase. Manage or cancel your subscription in your Apple ID account settings.",
                "Android (Google Play): Payment is charged to your Google Play account at confirmation of purchase. Manage or cancel your subscription in Google Play Store subscription settings.",
            ],
            note: "If a free trial is offered, it converts to a paid subscription unless canceled at least 24 hours before the trial ends. Subscriptions automatically renew unless canceled at least 24 hours before the end of the current billing period. We do not receive your payment card details — billing is handled entirely by Apple or Google.",
        },
        {
            title: "7. AI-Generated Content Disclaimer",
            content:
                "Quran Chat Buddy provides AI-generated responses for informational and educational purposes only. The App does not provide religious rulings (fatwas), legal opinions, or scholarly advice. While we strive to provide accurate and respectful content, we do not guarantee the accuracy, completeness, or reliability of AI-generated responses. Users are encouraged to consult qualified Islamic scholars for authoritative religious guidance.",
        },
        {
            title: "8. Privacy Policy",
            content:
                "Your use of the App is also governed by our Privacy Policy, which explains how we collect, use, and protect your information. Please review it at: https://quranchatbuddy.com/privacy-policy",
        },
        {
            title: "9. Limitation Of Liability",
            content:
                "To the fullest extent permitted by law, Quran Chat Buddy shall not be liable for any indirect, incidental, consequential, special, or punitive damages arising out of or related to your use of the App, including but not limited to reliance on AI-generated content.",
        },
        {
            title: "10. Contact Information",
            content: `If you have any questions about these Terms, please contact us at:`,
            contact: true,
        },
    ];

    return (
        <Box
            sx={{
                py: { xs: 6, md: 8 },
                backgroundColor: "background.default",
                minHeight: "calc(100vh - 200px)",
            }}
        >
            <SEO {...pageMeta.terms} />
            <Container maxWidth="md">
                <Fade in timeout={800}>
                    <Card
                        sx={{
                            overflow: "hidden",
                            boxShadow: "0 8px 32px rgba(46, 139, 87, 0.1)",
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                background:
                                    "linear-gradient(135deg, #2E8B57 0%, #228B22 100%)",
                                color: "white",
                                p: { xs: 4, md: 6 },
                                textAlign: "center",
                            }}
                        >
                            <GavelIcon sx={{ fontSize: 48, mb: 2 }} />
                            <Typography
                                variant="h2"
                                component="h1"
                                sx={{
                                    mb: 2,
                                    fontWeight: 700,
                                    fontSize: { xs: "2.5rem", md: "3rem" },
                                }}
                            >
                                Terms of Service
                            </Typography>
                            <Chip
                                label="Last updated: October 4, 2025"
                                sx={{
                                    bgcolor: "rgba(255, 255, 255, 0.2)",
                                    color: "white",
                                    fontWeight: 500,
                                }}
                            />
                        </Box>

                        {/* Content */}
                        <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                            <Box sx={{ mb: 4 }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: "1.1rem",
                                        lineHeight: 1.8,
                                        color: "text.secondary",
                                        textAlign: "center",
                                        fontStyle: "italic",
                                    }}
                                >
                                    These terms govern your use of Quran Chat
                                    Buddy and outline our mutual
                                    responsibilities.
                                </Typography>
                            </Box>

                            {sections.map((section, index) => (
                                <Fade
                                    in
                                    timeout={1000 + index * 200}
                                    key={index}
                                >
                                    <Box sx={{ mb: 4 }}>
                                        <Typography
                                            variant="h4"
                                            component="h2"
                                            sx={{
                                                mb: 2,
                                                fontWeight: 600,
                                                color: "primary.main",
                                                position: "relative",
                                                paddingBottom: 1,
                                                "&::after": {
                                                    content: '""',
                                                    position: "absolute",
                                                    bottom: 0,
                                                    left: 0,
                                                    width: 60,
                                                    height: 3,
                                                    background:
                                                        "linear-gradient(90deg, #2E8B57, #DAA520)",
                                                    borderRadius: 1,
                                                },
                                            }}
                                        >
                                            {section.title}
                                        </Typography>

                                        <Typography
                                            variant="body1"
                                            sx={{
                                                mb: section.list ? 2 : 0,
                                                lineHeight: 1.8,
                                                color: "text.secondary",
                                            }}
                                        >
                                            {section.content}
                                        </Typography>

                                        {section.list && (
                                            <List dense>
                                                {section.list.map(
                                                    (item, itemIndex) => (
                                                        <ListItem
                                                            key={itemIndex}
                                                            sx={{ py: 0.5 }}
                                                        >
                                                            <ListItemText
                                                                primary={`• ${item}`}
                                                                primaryTypographyProps={{
                                                                    variant:
                                                                        "body1",
                                                                    color: "text.secondary",
                                                                    lineHeight: 1.6,
                                                                }}
                                                            />
                                                        </ListItem>
                                                    ),
                                                )}
                                            </List>
                                        )}

                                        {section.note && (
                                            <Alert
                                                icon={
                                                    <InfoIcon fontSize="inherit" />
                                                }
                                                severity="info"
                                                sx={{
                                                    mt: 2,
                                                    borderRadius: 2,
                                                }}
                                            >
                                                {section.note}
                                            </Alert>
                                        )}

                                        {section.contact && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 3,
                                                    bgcolor: "primary.light",
                                                    borderRadius: 2,
                                                    color: "white",
                                                }}
                                            >
                                                <ContactIcon sx={{ mb: 1 }} />
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        mb: 1,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    Email:{" "}
                                                    <Box
                                                        component="a"
                                                        href="mailto:admin@quranchatbuddy.com"
                                                        sx={{
                                                            color: "inherit",
                                                            textDecoration:
                                                                "underline",
                                                        }}
                                                    >
                                                        admin@quranchatbuddy.com
                                                    </Box>
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 500 }}
                                                >
                                                    Support:{" "}
                                                    <Box
                                                        component="a"
                                                        href="mailto:admin@quranchatbuddy.com"
                                                        sx={{
                                                            color: "inherit",
                                                            textDecoration:
                                                                "underline",
                                                        }}
                                                    >
                                                        admin@quranchatbuddy.com
                                                    </Box>
                                                </Typography>
                                            </Box>
                                        )}

                                        {index < sections.length - 1 && (
                                            <Divider
                                                sx={{ mt: 4, opacity: 0.3 }}
                                            />
                                        )}
                                    </Box>
                                </Fade>
                            ))}

                            <Box
                                sx={{
                                    mt: 6,
                                    p: 3,
                                    bgcolor: "background.paper",
                                    borderRadius: 2,
                                    textAlign: "center",
                                    border: "1px solid",
                                    borderColor: "primary.light",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontStyle: "italic",
                                        color: "text.secondary",
                                    }}
                                >
                                    By using Quran Chat Buddy, you acknowledge
                                    that you have read, understood, and agree to
                                    be bound by these Terms of Service.
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Fade>
            </Container>
        </Box>
    );
};

export default TermsOfService;
